import { useState, useEffect } from 'react'
import API from '../services/api.js'

export function usePolicies(assetId) {
  const [policies, setPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (assetId) {
      fetchPolicies()
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId])

  useEffect(() => {
    if (!assetId || !policies.some(p => ['uploaded', 'parsing', 'mapping'].includes(p.status))) return
    const interval = setInterval(() => fetchPolicies(true), 10000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, policies])

  async function fetchPolicies(silent = false) {
    if (!silent) setLoading(true)
    try {
      const { data } = await API.get('/policies/', { params: { asset_id: assetId } })
      setPolicies(data || [])
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  async function uploadPolicy(file, assetIdParam, documentType = 'Policy') {
    if (!assetIdParam) {
      throw new Error('No asset selected. Please choose an asset before uploading.')
    }

    const fileExtension = file.name.split('.').pop().toLowerCase()
    const allowedExtensions = ['pdf', 'docx', 'txt', 'xlsx']

    if (!allowedExtensions.includes(fileExtension)) {
      throw new Error('Invalid file type. Allowed: PDF, DOCX, TXT, XLSX')
    }

    const maxSize = 50 * 1024 * 1024
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 50MB')
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('asset_id', assetIdParam)
    formData.append('document_type', documentType)

    try {
      const { data: policy } = await API.post('/policies/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setPolicies(prev => [policy, ...prev])
      return policy
    } catch (err) {
      await fetchPolicies(true)
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function startMapping(policyId, frameworkIds) {
    try {
      const { data } = await API.post(`/policies/${policyId}/start-mapping/`, {
        framework_ids: frameworkIds,
        asset_id: assetId,
      })
      return data
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function retryParse(policyId) {
    try {
      const { data } = await API.post(`/policies/${policyId}/retry-parse/`, {
        asset_id: assetId,
      })
      setPolicies(prev => prev.map(p => (p.id === policyId ? data : p)))
      return data
    } catch (err) {
      await fetchPolicies(true)
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  return { policies, loading, error, uploadPolicy, startMapping, retryParse, refetch: fetchPolicies }
}
